import React, { useEffect, useState } from 'react';

import { Stack, Typography, Box } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { Search } from 'components/Search';
import { BreadCrumbs } from 'components/Breadcrumbs';
import { ContentLayout } from 'components/Layout';
import { Loader } from 'components/Loader';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';
import { DetailViewTabList } from 'components/TabList';

import { useSubscriptions } from '../api/getAllSubscriptions';
import { SubscriptionsListItem } from '../components/SubscriptionsListItems';
import { useInvoices } from 'features/invoices/api/getAllInvoices';
import { InvoicesListItem } from 'features/invoices/components/InvoicesListItem';
import { useAuth } from 'lib/auth';

export const List = ({ companyType }) => {
  const { account } = useAuth();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState('subscriptions');
  const [search, setSearch] = useState('');

  const {
    data: { subscriptions, total },
    refetch: refetchSubscritions,
  } = useSubscriptions({
    page: page - 1,
    limit: pageSize,
    queryOptions: { onSettled: () => setLoading(false), enabled: false },
  });

  const {
    data: { invoices, total: invoicesTotal },
    isFetching: isFetchingInvoices,
  } = useInvoices({
    page: page - 1,
    limit: pageSize,
    search,
    queryOptions: { enabled: currentTab === 'job_ads' },
  });

  const handlePageChange = (_, page) => setPage(page);

  const debouncedSearchChange = useDebouncedCallback((search) => setSearch(search), 300);
  const handleSearchChange = ({ target: { value: search } }) => {
    debouncedSearchChange(search);
  };

  useSubscriptionsRefetch();

  const labelName = 'Subscriptions';

  return (
    <>
      {account.super_admin && (
        <ContentLayout>
          <Stack height={1}>
            <Stack>
              <BreadCrumbs
                items={[
                  { name: '', to: '' },
                  { name: 'Payments' },
                ]}
              />
            </Stack>

            <Box
              sx={{
                height: 16,
                backgroundColor: 'background.paper',
              }}
            />

            <DetailViewTabList
              value={currentTab}
              onChange={(_, newTab) => {
                setPage(1);
                setCurrentTab(newTab);
              }}
              tabs={[
                { label: 'Subscriptions', value: 'subscriptions' },
                { label: 'Job Ads', value: 'job_ads' },
              ]}
            />

            {currentTab === 'subscriptions' && (
              <Stack
                px={4}
                py={2}
                bgcolor="background.paper"
                borderTop="1px solid"
                borderColor="border.main"
                height="100%"
              >
                {!subscriptions && (
                  <Stack
                    height="100%"
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="h5">No {labelName} added</Typography>
                  </Stack>
                )}

                {subscriptions && (
                  <Stack
                    flex={1}
                    mt={0.5}
                    py={2}
                    bgcolor="background.paper"
                    borderTop="1px solid"
                    borderColor="border.main"
                    gap={1}
                  >
                    {subscriptions.length !== 0 &&
                      subscriptions.map((item) => <SubscriptionsListItem key={item.id} {...item} />)}
                  </Stack>
                )}
                {subscriptions && subscriptions.length !== 0 && (
                  <Stack direction="row" justifyContent="center">
                    <Pagination
                      page={page}
                      handlePageChange={handlePageChange}
                      itemCount={total}
                      itemsPerPage={pageSize}
                      onItemsPerPageChange={setPageSize}
                    />
                  </Stack>
                )}
              </Stack>
            )}

            {currentTab === 'job_ads' && (
              <Stack
                px={4}
                py={2}
                bgcolor="background.paper"
                borderTop="1px solid"
                borderColor="border.main"
                height="100%"
              >
                <Search handleSearchChange={handleSearchChange} sx={{ minWidth: 200, width: 270, mb: 2 }} />

                {isFetchingInvoices ? (
                  <Loader />
                ) : !invoices || invoices.length === 0 ? (
                  <Stack
                    height="100%"
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="h5">No invoices found</Typography>
                  </Stack>
                ) : (
                  <>
                    <Stack
                      flex={1}
                      mt={0.5}
                      py={2}
                      bgcolor="background.paper"
                      borderTop="1px solid"
                      borderColor="border.main"
                      gap={1}
                    >
                      {invoices.map((item) => (
                        <InvoicesListItem key={item.id} {...item} />
                      ))}
                    </Stack>
                    <Stack direction="row" justifyContent="center">
                      <Pagination
                        page={page}
                        handlePageChange={handlePageChange}
                        itemCount={invoicesTotal}
                        itemsPerPage={pageSize}
                        onItemsPerPageChange={setPageSize}
                      />
                    </Stack>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        </ContentLayout>
      )}
    </>
  );

  function useSubscriptionsRefetch() {
    useEffect(() => {
      if (currentTab === 'subscriptions') {
        refetchSubscritions();
      }
    }, [page, pageSize, currentTab]);
  }
};
